import { Alert, Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  framework_code: string;
  name: string;
  kind: string;
  authority_name?: string | null;
  is_readiness_only: boolean;
  status: string;
  version_count: number;
};

export default function StandardsListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.standards.manage') || user?.permissions?.includes('education.admin')
  );
  const [kind, setKind] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-standards', kind, status, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/standards/frameworks', {
        params: { kind, status, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('standards')}
      description={t('standardsIntro')}
      extra={
        canManage ? (
          <Button type="primary" onClick={() => navigate('/education/standards/new')}>
            {t('newStandardFramework')}
          </Button>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('standardsReadinessHint')} />
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('frameworkKind')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={kind}
                onChange={(v) => setKind(v || undefined)}
                options={[
                  { value: 'national', label: t('kindNational') },
                  { value: 'board', label: t('kindBoard') },
                  { value: 'state', label: t('kindState') },
                  { value: 'quality', label: t('kindQuality') },
                  { value: 'readiness', label: t('kindReadiness') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 120 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'active', label: t('statusActive') },
                  { value: 'retired', label: t('statusRetired') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/standards/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('frameworkCode'), dataIndex: 'framework_code', width: 180 },
              { title: t('name'), dataIndex: 'name' },
              { title: t('frameworkKind'), dataIndex: 'kind', width: 110 },
              {
                title: t('readinessOnly'),
                dataIndex: 'is_readiness_only',
                width: 110,
                render: (v: boolean) => (v ? <Tag color="orange">{t('yes')}</Tag> : '—'),
              },
              { title: t('versions'), dataIndex: 'version_count', width: 90 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
